import React, { Ref, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    clientCodeState,
    isDecreaseZoomControlAmountState,
    isDrawPolygonActiveState,
    isDrawPolylineActiveState,
    isEarthEngineServiceActiveState,
    isEditableState,
    isEditingZoomState,
    isHideMapTilesState,
    isOnMapClickState,
    isSendAnimationEndState,
    isShowDetailsButtonState,
    isUsingMeasuringToolState,
    mapCenterState,
    mapIframeIdState,
    mapOptionsState,
    mapZoomLevelState,
} from '../../../app/redux/retrievers/retrievers';
import { Map } from 'leaflet';
import { MapContainer, useMapEvents } from 'react-leaflet';
import { ParentEventListenerHandler } from './events/event-listeners/ParentEventListenerHandler';
import {
    EditLayer,
    MarkerLayer,
    PolylineLayer,
    PolygonLayerUneditable,
    TilesLayer,
    SquareGridLayer,
} from './imports/map.layers.imports';
import MapCoordinatesDisplay from '../components/MapCoordinates/MapCoordinatesDisplay/MapCoordinatesDisplay';
import MapTilesTabs from '../components/MapTiles/MapTilesTabs/MapTilesTabs';
import PolygonBreakpoints from '../components/Polygons/PolygonBreakpoints/PolygonBreakpoints';
import MeasuringTool from '../components/MeasuringTool/MeasuringTool';
import AccessDenied from '../components/Overlays/AccessDenied/AccessDenied';
import Attribution from '../components/Attribution/Attribution';
import LoadingOverlay from '../components/Overlays/LoadingOverlay/LoadingOverlay';
import MapLockOverlay from '../components/Overlays/MapLockOverlay/MapLockOverlay';
import SearchBar from '../components/SearchBar/SearchBar';
import Compass from '../components/Compass/Compass';
import Modal from '../../common/Modal/Modal';
import RotationControl from './controllers/RotationControl/RotationControl';
import { removeMapButtons, removeZoomButtons, restoreZoomButtons } from './functions/removeMapButtons';
import { sendDataToParent } from './functions/sendDataToParent';
import { getMapEventsObject } from './functions/getMapEventsObject';
import { AnimationControl } from './controllers/AnimationControl';
import { MapLockControl } from './controllers/MapLockControl';
import FullscreenControl from './controllers/FullscreenControl';
import OverlaysController from '../Layers/TilesLayer/Overlays/OverlaysController';
import { callUseMapApi } from '../../../core/data/api/map-permission/get/callUseMapApi';
import { mapContainerProps } from './props/mapContainerProps';
import ActionsButtons from '../components/ActionsButtons/ActionsButtons';
import RegionMarkerLayer from '../Layers/provinceCountyMarkerLayer/regionMarkerLayer';

import { MapLeafletContainerStyles } from './styles/MapLeafletContainer.styles';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import 'leaflet-fullscreen/dist/leaflet.fullscreen.css';
import 'leaflet-fullscreen/dist/Leaflet.fullscreen';
import 'leaflet-rotate';
import '../styles//map.custom.styles.css';

const isFreeAccess = process.env.REACT_APP_FREE_ACCESS === 'true';

const MapLeafletContainer = () => {
    const mapRef = useRef() as Ref<Map>;
    const dispatch = useDispatch();
    /*----redux states----*/
    const clientCode = useSelector(clientCodeState);
    const mapIframeId = useSelector(mapIframeIdState);
    const mapOptions = useSelector(mapOptionsState);
    const mapCenterCoords = useSelector(mapCenterState);
    const mapZoomLevel = useSelector(mapZoomLevelState);
    const isDrawPolygonActive = useSelector(isDrawPolygonActiveState);
    const isDrawPolylineActive = useSelector(isDrawPolylineActiveState);
    const isUsingMeasuringTool = useSelector(isUsingMeasuringToolState);
    const isDecreaseZoomControlAmount = useSelector(isDecreaseZoomControlAmountState);
    const isSendAnimationEnd = useSelector(isSendAnimationEndState);
    const isHideMapTiles = useSelector(isHideMapTilesState);
    const isEditable = useSelector(isEditableState);
    const isEditingZoom = useSelector(isEditingZoomState);
    const isOnMapClick = useSelector(isOnMapClickState);

    /*----react states----*/
    const [mapContainerKey, setMapContainerKey] = useState(0);
    const [isGetCurrentCenterAndZoom, setIsGetCurrentCenterAndZoom] = useState<boolean>(false);
    const [isGetCurrentBoundingBox, setIsGetCurrentBoundingBox] = useState<boolean>(false);
    const [isUserAuthorized, setIsUserAuthorized] = useState(true);
    const [isLoading, setIsLoading] = useState(true);

    const isMapLocked = mapOptions.mapLocked;
    const isEditControlActive = mapOptions.editControl;
    const canZoom = mapOptions.attributes.canZoom;

    const MapEvents = () => {
        useMapEvents(
            getMapEventsObject(
                dispatch,
                mapIframeId,
                isDrawPolylineActive,
                isDrawPolygonActive,
                isGetCurrentCenterAndZoom,
                isGetCurrentBoundingBox,
                isUsingMeasuringTool,
                isSendAnimationEnd,
                isOnMapClick,
                setIsLoading,
            ),
        );
        return null;
    };

    /*-------------------------------------window event listener initiation-------------------------------------*/
    useEffect(() => {
        sendDataToParent('map-mounted');
        window.addEventListener('message', (e) =>
            ParentEventListenerHandler(e, dispatch, setIsGetCurrentCenterAndZoom, setIsGetCurrentBoundingBox),
        );
        return () => {
            window.removeEventListener('message', (e) =>
                ParentEventListenerHandler(e, dispatch, setIsGetCurrentCenterAndZoom, setIsGetCurrentBoundingBox),
            );
        };
    }, [dispatch]);
    /*----------------------------------------------------------------------------------------------------------*/

    /*----remove map buttons if map is locked or remove zoom buttons if zooming is disabled----*/
    useEffect(() => {
        if (isMapLocked && !isEditingZoom) {
            removeMapButtons();
        } else if (isMapLocked && isEditingZoom) {
            restoreZoomButtons();
        }
        if (!canZoom) {
            removeZoomButtons();
        }
    }, [isMapLocked, canZoom, isEditingZoom, isLoading]);
    /*----------------------------------------------------------------------------------------*/

    /*----------------------map tiles permission API call----------------------*/
    useEffect(() => {
        if (!isFreeAccess && !isHideMapTiles) {
            if (clientCode !== '0') {
                callUseMapApi(clientCode, setIsUserAuthorized, setIsLoading);
            }
        } else {
            setIsLoading(false);
        }
    }, [clientCode, isHideMapTiles]);
    /*------------------------------------------------------------------------*/

    /*--------map key change based on zoom increment decresase and map id-----*/
    useEffect(() => {
        if (isDecreaseZoomControlAmount || mapIframeId !== 'mapiframe') {
            setMapContainerKey(mapContainerKey + 1 * Math.random());
        }
    }, [mapIframeId, isDecreaseZoomControlAmount, canZoom]);
    /*-----------------------------------------------------------------------*/

    return (
        <>
            {/*----overlays----*/}
            <MapLockOverlay isUserAuthorized={isUserAuthorized} isMapLocked={isMapLocked} />
            {!isUserAuthorized && <AccessDenied />}
            {/*----container----*/}
            <div style={MapLeafletContainerStyles.Holder}>
                <MapContainer
                    id={mapIframeId}
                    key={!isMapLocked || isEditingZoom ? mapContainerKey : 0}
                    ref={mapRef}
                    {...mapContainerProps(mapCenterCoords, mapZoomLevel, isDecreaseZoomControlAmount, isLoading, mapOptions)}
                >
                    {isUserAuthorized && (
                        <>
                            <LoadingOverlay isLoading={isLoading} />
                            <MapEvents />
                            <Modal />
                            {/* <SearchBar /> */}
                            {/*----map controllers----*/}
                            <AnimationControl mapCenterCoords={mapCenterCoords} zoom={mapZoomLevel} />
                            {!isMapLocked && <FullscreenControl mapRef={mapRef} />}
                            {!isMapLocked && <RotationControl mapRef={mapRef} />}
                            <OverlaysController />
                            {/* <MapLockControl /> */}
                            {/*----map tiles----*/}
                            {<TilesLayer />}
                            {/*----map services----*/}
                            {/* {isEarthEngineServiceActive && <EarthEngineService />} */}
                            {/*----editing----*/}
                            {!isMapLocked && isEditControlActive && !isFreeAccess && (
                                <EditLayer mapRef={mapRef} isMapLocked={isMapLocked} />
                            )}
                            {/*----polylines----*/}
                            {<PolylineLayer />}
                            {/*---polygons uneditable---*/}
                            {<PolygonLayerUneditable isEditable={isEditable} />}
                            {/*----markers----*/}
                            {<MarkerLayer />}
                            {<RegionMarkerLayer />}
                            {/*----square grid----*/}
                            <SquareGridLayer />
                            {/*---custom interactive components---*/}
                            {!isLoading && isUserAuthorized && !isFreeAccess && <MapTilesTabs />}
                            {/*----custom non-interactive components----*/}
                            <PolygonBreakpoints />
                            {!isMapLocked && <MapCoordinatesDisplay />}
                            <MeasuringTool />
                            <Compass />
                            {/*----attribution----*/}
                            <Attribution />
                            {/*----actionsButtons----*/}
                            <ActionsButtons />
                        </>
                    )}
                </MapContainer>
            </div>
        </>
    );
};

export default MapLeafletContainer;
